import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '@/lib/cx';

export interface TabItem {
  key: string;
  label: ReactNode;
  /** 件数・エラー数などの小さなバッジ */
  badge?: ReactNode;
  disabled?: boolean;
  /** パネル内容。省略時は children(activeKey) を使う */
  content?: ReactNode;
}

export interface TabsProps {
  items: ReadonlyArray<TabItem>;
  value?: string;
  defaultValue?: string;
  onChange?: (key: string) => void;
  /** content を持たない場合に、選択キーから内容を描画 */
  children?: (activeKey: string) => ReactNode;
  className?: string;
}

/** タブ。矢印キー・Home/End で移動できる（WAI-ARIA Tabs パターン） */
export function Tabs({ items, value, defaultValue, onChange, children, className }: TabsProps) {
  const baseId = useId();
  const [inner, setInner] = useState(defaultValue ?? items[0]?.key ?? '');
  const active = value ?? inner;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const select = (key: string) => {
    if (value === undefined) setInner(key);
    onChange?.(key);
  };

  const enabled = items.filter((i) => !i.disabled);
  const onKeyDown = (e: KeyboardEvent) => {
    const idx = enabled.findIndex((i) => i.key === active);
    let next: TabItem | undefined;
    if (e.key === 'ArrowRight') next = enabled[(idx + 1) % enabled.length];
    else if (e.key === 'ArrowLeft') next = enabled[(idx - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') next = enabled[0];
    else if (e.key === 'End') next = enabled[enabled.length - 1];
    if (next) {
      e.preventDefault();
      select(next.key);
      refs.current[next.key]?.focus();
    }
  };

  const activeItem = items.find((i) => i.key === active);
  return (
    <div className={cx('bs-tabs', className)}>
      <div role="tablist" className="bs-tabs__list" onKeyDown={onKeyDown}>
        {items.map((it) => {
          const selected = it.key === active;
          return (
            <button
              key={it.key}
              ref={(el) => {
                refs.current[it.key] = el;
              }}
              role="tab"
              type="button"
              id={`${baseId}-tab-${it.key}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${it.key}`}
              tabIndex={selected ? 0 : -1}
              disabled={it.disabled}
              className={cx('bs-tabs__tab', selected && 'is-active')}
              onClick={() => select(it.key)}
            >
              {it.label}
              {it.badge != null && <span className="bs-tabs__badge">{it.badge}</span>}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`${baseId}-panel-${active}`} aria-labelledby={`${baseId}-tab-${active}`} className="bs-tabs__panel">
        {activeItem?.content ?? children?.(active)}
      </div>
    </div>
  );
}
